import { useEffect, useState } from "react";
import { useKmsService } from "../providers/kms-service-provider";
import { TarifFixeCourse } from "../types/models/tarif-fixe-course";

type UseTarifsFixesParams = {
  /**
   * Le back-office réserve pour n'importe quel client : il lui faut donc
   * l'entreprise choisie dans le formulaire. Un client, lui, ne voit que les
   * tarifs de son entreprise active, que le backend résout depuis son jeton.
   */
  isAdmin: boolean;
  idEntreprise?: number | string | null;
  /** Faux en édition : on ne réserve pas sur un tarif depuis une course existante. */
  actif?: boolean;
};

/**
 * Tarifs fixes réservables. Toujours filtrés sur les tarifs actifs : un tarif
 * désactivé reste visible au back-office mais n'est plus réservable.
 */
export const useTarifsFixes = ({
  isAdmin,
  idEntreprise,
  actif = true,
}: UseTarifsFixesParams) => {
  const { tarifFixeService } = useKmsService();

  const [tarifsFixes, setTarifsFixes] = useState<TarifFixeCourse[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!actif) {
      setTarifsFixes([]);
      return;
    }

    // Côté admin, tant qu'aucune entreprise n'est choisie il n'y a pas de
    // tarif négocié applicable : inutile d'interroger le backend.
    if (isAdmin && !idEntreprise) {
      setTarifsFixes([]);
      return;
    }

    let annule = false;
    setIsLoading(true);

    const requete = isAdmin
      ? tarifFixeService.getTarifsFixesByEntreprise(Number(idEntreprise), true)
      : tarifFixeService.getMesTarifsFixes();

    requete
      .then((resultat) => {
        if (!annule) setTarifsFixes(resultat);
      })
      .catch((erreur) => {
        if (annule) return;

        setTarifsFixes([]);
        // Cote client, l'echec est attendu et sans consequence : un compte sans
        // entreprise active n'a aucun tarif negocie. Cote back-office il traduit
        // un vrai probleme (droits, reseau) qu'il ne faut pas avaler — sinon la
        // section parait vide et la fonction semble ne pas exister.
        if (isAdmin) console.error("Chargement des tarifs fixes:", erreur);
      })
      .finally(() => {
        if (!annule) setIsLoading(false);
      });

    return () => {
      annule = true;
    };
  }, [tarifFixeService, isAdmin, idEntreprise, actif]);

  return { tarifsFixes, isLoading };
};
